export function formatCurrency(val) {
  if (val === null || val === undefined) return '--';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
}

export function formatNumber(val) {
  if (val === null || val === undefined) return '--';
  if (Math.abs(val) >= 1000000) return (val / 1000000).toFixed(1) + 'M';
  if (Math.abs(val) >= 1000) return (val / 1000).toFixed(1) + 'K';
  return Number(val).toLocaleString('en-US');
}

export function formatPercent(val) {
  if (val === null || val === undefined) return '--';
  return val.toFixed(1) + '%';
}

export function formatDate(dateStr) {
  if (!dateStr) return '--';
  const d = new Date(dateStr.includes('T') || dateStr.includes(' ') ? dateStr : dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatTime(timeStr) {
  if (!timeStr) return '--';
  const [h, m] = timeStr.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
}

// Compact wall-clock duration for fill time: "8m", "1h 12m", "45s".
// Null/undefined (an unmeasured or untrustworthy span) renders as the same
// '--' every other formatter here uses for missing data.
export function formatDuration(seconds) {
  if (seconds === null || seconds === undefined || !Number.isFinite(Number(seconds))) return '--';
  const total = Math.max(0, Math.round(Number(seconds)));
  if (total < 60) return `${total}s`;
  const mins = Math.floor(total / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  return remMins === 0 ? `${hrs}h` : `${hrs}h ${remMins}m`;
}

// Ticking-clock form for a break that is still running: "12:04", "1:02:11".
// Distinct from formatDuration on purpose — a live counter should move every
// second, where a settled duration reads better rounded to "12m".
export function formatClock(seconds) {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

export function formatDateTime(dateStr) {
  if (!dateStr) return '--';
  const d = new Date(dateStr);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
}

export function formatTimeAgo(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const d = new Date(dateStr);
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateStr);
}

export function todayET() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
}

// `includeFuture` widens the presets that would otherwise stop at today so
// scheduled (future-dated) rows stay visible: 'week' / 'month' / 'year' run to
// the end of the period, and 'lifetime' becomes open-ended (empty endDate = no
// upper bound). Presets that name an explicitly past window ('yesterday',
// 'last_7d', ...) never change. Used by the Breaks page, where breaks are
// scheduled ahead of time; reporting pages leave it off.
export function getDatePreset(preset, { includeFuture = false } = {}) {
  const today = todayET();
  const t = new Date(today + 'T12:00:00');
  // Anchor at noon so toISOString() can't roll the date backward.
  const iso = (y, m, d) => new Date(y, m, d, 12).toISOString().split('T')[0];
  switch (preset) {
    case 'today': return { startDate: today, endDate: today };
    case 'yesterday': {
      const y = new Date(t);
      y.setDate(y.getDate() - 1);
      const yd = y.toISOString().split('T')[0];
      return { startDate: yd, endDate: yd };
    }
    case 'week': {
      const start = new Date(t);
      start.setDate(start.getDate() - start.getDay());
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return {
        startDate: start.toISOString().split('T')[0],
        endDate: includeFuture ? end.toISOString().split('T')[0] : today,
      };
    }
    case 'month': {
      const start = `${today.substring(0, 7)}-01`;
      const end = includeFuture ? iso(t.getFullYear(), t.getMonth() + 1, 0) : today;
      return { startDate: start, endDate: end };
    }
    case 'last_month': {
      const lm = new Date(t.getFullYear(), t.getMonth() - 1, 1);
      const lmEnd = new Date(t.getFullYear(), t.getMonth(), 0);
      return { startDate: lm.toISOString().split('T')[0], endDate: lmEnd.toISOString().split('T')[0] };
    }
    case 'last_7d': {
      const end = new Date(t); end.setDate(end.getDate() - 1);
      const start = new Date(end); start.setDate(start.getDate() - 6);
      return { startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] };
    }
    case 'last_30d': {
      const end = new Date(t); end.setDate(end.getDate() - 1);
      const start = new Date(end); start.setDate(start.getDate() - 29);
      return { startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] };
    }
    case 'last_90d': {
      const end = new Date(t); end.setDate(end.getDate() - 1);
      const start = new Date(end); start.setDate(start.getDate() - 89);
      return { startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] };
    }
    case 'year': {
      const start = `${today.substring(0, 4)}-01-01`;
      const end = includeFuture ? `${today.substring(0, 4)}-12-31` : today;
      return { startDate: start, endDate: end };
    }
    case 'lifetime': return { startDate: '2025-05-01', endDate: includeFuture ? '' : today };
    default: return { startDate: today, endDate: today };
  }
}

const ORDINALS = ['th', 'st', 'nd', 'rd'];

// "August 29th, 2026" — the long form used on exported files.
export function formatLongDateOrdinal(d) {
  const day = d.getDate();
  const suffix = ORDINALS[(day % 100 - 20) % 10] || ORDINALS[day % 100] || ORDINALS[0];
  const month = d.toLocaleDateString('en-US', { month: 'long', timeZone: 'America/New_York' });
  const year = d.toLocaleDateString('en-US', { year: 'numeric', timeZone: 'America/New_York' });
  return `${month} ${day}${suffix}, ${year}`;
}

// Stamp for the footer of an exported chart or table. Eastern, like the rest
// of the app, and labelled so nobody has to guess.
export function formatPulledStamp(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', month: 'long', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  }).formatToParts(now);
  const get = t => parts.find(p => p.type === t)?.value;
  const day = parseInt(get('day'), 10);
  const suffix = ORDINALS[(day % 100 - 20) % 10] || ORDINALS[day % 100] || ORDINALS[0];
  return `Pulled on ${get('month')} ${day}${suffix}, ${get('year')} at ${get('hour')}:${get('minute')} ${get('dayPeriod')} ET`;
}

// "May 1, 2025 - Aug 29, 2026", or a single day, or an open-ended range.
export function formatRangeLabel(range) {
  if (!range || !range.startDate) return '';
  const start = formatDate(range.startDate);
  if (!range.endDate) return `${start} - onward`;
  if (range.startDate === range.endDate) return start;
  return `${start} - ${formatDate(range.endDate)}`;
}
